import { useState } from "react";
import { useForm } from "@mantine/form";
import {
  Alert,
  Container,
  TextInput,
  Text,
  Paper,
  Group,
  Button,
  Anchor,
  Stack,
} from "@mantine/core";
import { useStore } from "../store";
import { requestPasswordReset } from "./authApi";
import { Link } from "wouter";
import { IconInfoCircle } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import LangSelector from "./LangSelector";

export default function ForgotPassword({ realm, hideLangSelector }) {
  const [isFetching, setIsFetching] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const authToken = useStore((state) => state.authToken);
  const { t } = useTranslation("Auth");

  const form = useForm({
    initialValues: {
      emailAddress: "",
      realm: realm,
    },
    validate: {
      emailAddress: (val) =>
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val)
          ? null
          : t("invalid_email"),
    },
  });

  if (authToken) {
    return (
      <Container size="sm" mt="xl">
        <Paper radius="md" p="xl" withBorder>
          <Text size="lg" fw={500}>
            {t("you_are_logged_in")}
          </Text>
        </Paper>
      </Container>
    );
  }

  if (success) {
    return (
      <Container size="sm" mt="xl">
        <Paper radius="md" p="xl" withBorder>
          <Text size="lg" mb="lg" fw={500}>
            {t("password_reset_requested")}
          </Text>
          <Text>{t("password_reset_requested_message")}</Text>
        </Paper>
      </Container>
    );
  }

  const errorIcon = <IconInfoCircle />;

  return (
    <Container size="xs" mt="xl">
      {errorMessage && (
        <Alert
          variant="light"
          color="red"
          title="Password Recovery Failed"
          mb="lg"
          icon={errorIcon}
        >
          {errorMessage}
        </Alert>
      )}

      <Paper radius="md" p="xl" withBorder>
        <Group align="flex-start" justify="space-between">
          <Text size="lg" mb="xl" fw={500}>
            {t("recover_password")}
          </Text>
          {!hideLangSelector && <LangSelector />}
        </Group>

        <form
          onSubmit={form.onSubmit((values) => {
            setIsFetching(true);
            setErrorMessage(null);

            requestPasswordReset({ ...values })
              .then(() => {
                setIsFetching(false);
                setSuccess(true);
              })
              .catch((err) => {
                setIsFetching(false);
                setErrorMessage("" + err);
              });
          })}
        >
          <Stack>
            <TextInput
              required
              label={t("email")}
              disabled={isFetching}
              placeholder="john@example.com"
              value={form.values.emailAddress}
              onChange={(event) =>
                form.setFieldValue("emailAddress", event.currentTarget.value)
              }
              error={form.errors.emailAddress && t("invalid_email")}
              radius="md"
            />

            <Group justify="space-between" mt="xl">
              <Anchor component={Link} href="/auth/login" c="dimmed" size="xs">
                {t("login_instead")}
              </Anchor>
              <Button type="submit" radius="xl" loading={isFetching}>
                {t("request_reset_email")}
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
}
